import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function AdminDashboard() {
  const [view, setView] = useState('operators'); // operators, codes
  const [operators, setOperators] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateOperator, setShowCreateOperator] = useState(false);
  const [showGenerateCode, setShowGenerateCode] = useState(false);

  const token = localStorage.getItem('operator_token');

  useEffect(() => {
    fetchOperators();
    fetchActivationCodes();
  }, []);

  const fetchOperators = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/operators`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setOperators(data.operators || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch operators:', err);
      setLoading(false);
    }
  };

  const fetchActivationCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/activation-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCodes(data.codes || []);
    } catch (err) {
      console.error('Failed to fetch codes:', err);
    }
  };

  const createOperator = async (operatorData) => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/operators`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(operatorData)
      });

      if (response.ok) {
        await fetchOperators(); // Refresh the list first
        setShowCreateOperator(false); // Then close modal
        alert('Operator created successfully!');
      } else {
        const error = await response.json();
        alert('Failed to create operator: ' + error.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const generateCode = async (operatorId, notes) => {
    try {
      const response = await fetch(`${API_URL}/v1/admin/activation-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ operator_id: operatorId, notes })
      });

      if (response.ok) {
        fetchActivationCodes();
        setShowGenerateCode(false);
        alert('Activation code generated!');
      } else {
        const error = await response.json();
        alert('Failed to generate code: ' + error.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const updateOperatorStatus = async (operatorId, status, reason) => {
    if (!window.confirm(`Are you sure you want to ${status} this operator?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/v1/admin/operators/${operatorId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, reason })
      });

      if (response.ok) {
        fetchOperators();
        alert(`Operator ${status} successfully`);
      } else {
        const error = await response.json();
        alert('Failed: ' + error.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const revokeCode = async (codeId, reason) => {
    if (!window.confirm('Are you sure you want to revoke this activation code?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/v1/admin/activation-codes/${codeId}/revoke`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        fetchActivationCodes();
        alert('Code revoked successfully');
      } else {
        const error = await response.json();
        alert('Failed: ' + error.error);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-8">
              <h1 className="text-2xl font-bold text-white">⚙️ Admin Panel</h1>
              <div className="flex space-x-4">
                <button
                  onClick={() => setView('operators')}
                  className={`px-3 py-2 rounded-md ${view === 'operators' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  Operators
                </button>
                <button
                  onClick={() => setView('codes')}
                  className={`px-3 py-2 rounded-md ${view === 'codes' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                  Activation Codes
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/operator/dashboard" className="text-gray-400 hover:text-white">
                ← Back to Operator Dashboard
              </a>
              <button
                onClick={() => {
                  localStorage.removeItem('operator_token');
                  window.location.href = '/operator/login';
                }}
                className="text-gray-400 hover:text-white"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Operators View */}
        {view === 'operators' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Operators</h2>
              <button
                onClick={() => setShowCreateOperator(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
              >
                + Create Operator
              </button>
            </div>

            {loading ? (
              <div className="text-center text-gray-400 py-12">Loading...</div>
            ) : (
              <div className="bg-slate-800 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-slate-700">
                  <thead className="bg-slate-750">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Admin</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {operators.map(op => (
                      <tr key={op.id} className="hover:bg-slate-750">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{op.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{op.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            op.status === 'active' ? 'bg-green-900 text-green-300' :
                            op.status === 'suspended' ? 'bg-yellow-900 text-yellow-300' :
                            'bg-red-900 text-red-300'
                          }`}>
                            {op.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {op.is_admin ? '✓ Yes' : 'No'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {new Date(op.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          {!op.is_admin && (
                            <div className="flex justify-end space-x-2">
                              {op.status === 'active' ? (
                                <button
                                  onClick={() => updateOperatorStatus(op.id, 'suspended', prompt('Reason for suspension:'))}
                                  className="text-yellow-400 hover:text-yellow-300"
                                >
                                  Suspend
                                </button>
                              ) : (
                                <button
                                  onClick={() => updateOperatorStatus(op.id, 'active', '')}
                                  className="text-green-400 hover:text-green-300"
                                >
                                  Activate
                                </button>
                              )}
                              <button
                                onClick={() => updateOperatorStatus(op.id, 'banned', prompt('Reason for ban:'))}
                                className="text-red-400 hover:text-red-300"
                              >
                                Ban
                              </button>
                              <button
                                onClick={() => {
                                  const notes = prompt('Notes for activation code (optional):');
                                  if (notes !== null) generateCode(op.id, notes);
                                }}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                Generate Code
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Activation Codes View */}
        {view === 'codes' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Activation Codes</h2>
              <button
                onClick={() => setShowGenerateCode(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
              >
                + Generate Code
              </button>
            </div>

            <div className="bg-slate-800 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-750">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Operator</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Notes</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {codes.map(code => (
                    <tr key={code.id} className="hover:bg-slate-750">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm text-blue-400 bg-slate-900 px-2 py-1 rounded">
                          {code.code}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {code.operator_name || code.operator_email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {code.revoked ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-red-900 text-red-300">Revoked</span>
                        ) : code.used ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-700 text-gray-300">Used</span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-900 text-green-300">Available</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {new Date(code.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {code.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {!code.revoked && !code.used && (
                          <button
                            onClick={() => revokeCode(code.id, prompt('Reason for revocation:'))}
                            className="text-red-400 hover:text-red-300"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Operator Modal */}
      {showCreateOperator && (
        <CreateOperatorModal
          onClose={() => setShowCreateOperator(false)}
          onCreate={createOperator}
        />
      )}

      {/* Generate Code Modal */}
      {showGenerateCode && (
        <GenerateCodeModal
          operators={operators.filter(op => op.status === 'active')}
          onClose={() => setShowGenerateCode(false)}
          onGenerate={generateCode}
        />
      )}
    </div>
  );
}

// Create Operator Modal Component
function CreateOperatorModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold text-white mb-4">Create New Operator</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Password</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Notes (optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-md"
                rows="3"
              />
            </div>
          </div>
          <div className="flex space-x-3 mt-6">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md"
            >
              Create Operator
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-md"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Generate Code Modal Component
function GenerateCodeModal({ operators, onClose, onGenerate }) {
  const [selectedOperator, setSelectedOperator] = useState(operators.length === 1 ? operators[0].id : '');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedOperator) {
      onGenerate(selectedOperator, notes);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold text-white mb-4">Generate Activation Code</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Select Operator</label>
              <select
                required
                value={selectedOperator}
                onChange={(e) => setSelectedOperator(e.target.value)}
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-md"
              >
                <option value="">-- Choose Operator --</option>
                {operators.map(op => (
                  <option key={op.id} value={op.id}>
                    {op.name} ({op.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-slate-700 text-white px-3 py-2 rounded-md"
                rows="3"
                placeholder="e.g., Server location, purpose, etc."
              />
            </div>
          </div>
          <div className="flex space-x-3 mt-6">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md"
            >
              Generate Code
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-md"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminDashboard;
